import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { EnvironmentService } from '@core/services/environment.service';
import {
    AssetImageConfiguration,
    LazyAssetLazyComponent,
} from '@features/lazy-image/components/lazy-asset-lazy/lazy-asset-lazy.component';
import { LazysizesService } from '@features/lazy-image/services/lazysizes.service';

@Component({
    selector: 'app-lazy-asset',
    templateUrl: './lazy-asset.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, LazyAssetLazyComponent],
})
export class LazyAssetComponent {
    private environmentService = inject(EnvironmentService);
    private sanitizer = inject(DomSanitizer);

    private lazysizesService = inject(LazysizesService);

    configuration = input.required<AssetImageConfiguration>();
    additionalClasses = input<string[] | string>([]);
    lazy = input<boolean>(true);
    lazySizesEnabled = this.lazysizesService.isEnabled;

    svgPlaceholder = computed(() => {
        const configuration = this.configuration();
        return this.generateSvgPlaceholder(configuration);
    });

    srcSet = computed(() => {
        const configuration = this.configuration();
        return [
            `${configuration.src} 1x`,
            configuration.src2x && `${configuration.src2x} 2x`,
            configuration.src3x && `${configuration.src3x} 3x`,
        ]
            .filter(Boolean)
            .join(', ');
    });

    defaultSrc = computed(() => this.configuration().src);

    altText = computed(() => this.configuration()?.alt || '');

    generateSvgPlaceholder(configuration: AssetImageConfiguration): SafeUrl {
        if (!configuration) return '';

        const ratioWidth = configuration.ratioWidth || 1;
        const ratioHeight = configuration.ratioHeight || 1;
        const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${ratioWidth} ${ratioHeight}'><rect height='100%' style='fill:${
            configuration.placeholderColor || '#e9e9e9'
        }' width='100%' x='0' y='0' /></svg>`;
        let encodedSvg = '';
        if (this.environmentService.isBrowser()) {
            encodedSvg = btoa(svg);
        } else {
            encodedSvg = Buffer.from(svg, 'binary').toString('base64');
        }
        return this.sanitizer.bypassSecurityTrustUrl(
            `data:image/svg+xml;base64,${encodedSvg}`
        );
    }
}
